import { useEffect, useState } from "react";
import { Check, Clock, Plus, X } from "lucide-react";
import { useReducedMotion, motion } from "motion/react";

import { useScreen0Language } from "@/features/language";
import { formatTemplate, getTranslations } from "@/i18n";
import { formatDisplayMinuteOfDay } from "../../lib/format";
import {
  MAX_TARGETS_PER_RULE,
  MINUTES_PER_DAY,
  WEEKDAYS_DISPLAY,
  formatDurationMinutes,
  isRuleDraftSaveable,
  normalizeRuleWindow,
  parseMinuteOfDay,
  windowEndMinute,
  windowFromStartEnd,
} from "../../lib/rules/ruleDerivation";
import { targetDisplaysSmartBlockBadge } from "../../lib/insights/smartBlockSelection";
import type { Rule, RuleDraft, Target } from "../../lib/rules/ruleTypes";
import { BottomSheet } from "../habits/BottomSheet";
import { TargetPreviewAssets, TargetPreviewMeta } from "./TargetPreview";

type WindowMode = "duration" | "endtime";

type RuleEditorSheetProps = {
  open: boolean;
  draft: RuleDraft;
  targets: Target[];
  rules?: Rule[];
  pending: boolean;
  onChange: (draft: RuleDraft) => void;
  onCreateTarget: () => void;
  onClose: () => void;
  onSave: () => void;
};

export function RuleEditorSheet({
  open,
  draft,
  targets,
  rules,
  pending,
  onChange,
  onCreateTarget,
  onClose,
  onSave,
}: RuleEditorSheetProps) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const [mode, setMode] = useState<WindowMode>("duration");
  const [targetsError, setTargetsError] = useState(false);
  const [limitNotice, setLimitNotice] = useState(false);
  const selected = new Set(draft.targetIds);
  const atLimit = selected.size >= MAX_TARGETS_PER_RULE;
  const targetsById = new Map(targets.map((target) => [target._id, target]));

  useEffect(() => {
    if (open) {
      setMode("duration");
      setTargetsError(false);
      setLimitNotice(false);
    }
  }, [open]);

  useEffect(() => {
    if (selected.size > 0 && targetsError) setTargetsError(false);
  }, [selected.size, targetsError]);

  useEffect(() => {
    if (!atLimit && limitNotice) setLimitNotice(false);
  }, [atLimit, limitNotice]);

  const toggleTarget = (id: string) => {
    if (selected.has(id)) {
      onChange({ ...draft, targetIds: draft.targetIds.filter((targetId) => targetId !== id) });
      return;
    }
    if (atLimit) {
      setLimitNotice(true);
      return;
    }
    onChange({ ...draft, targetIds: [...draft.targetIds, id] });
  };

  const toggleDay = (value: number) => {
    const has = draft.days.includes(value);
    if (has && draft.days.length === 1) return;
    const days = has ? draft.days.filter((day) => day !== value) : [...draft.days, value];
    onChange({ ...draft, days });
  };

  const setStart = (value: string) =>
    onChange({ ...draft, window: normalizeRuleWindow({ ...draft.window, startMinute: parseMinuteOfDay(value) }) });
  const setDuration = (minutes: number) =>
    onChange({ ...draft, window: normalizeRuleWindow({ ...draft.window, durationMinutes: minutes }) });
  const setEnd = (value: string) =>
    onChange({ ...draft, window: windowFromStartEnd(draft.window.startMinute, parseMinuteOfDay(value)) });

  const endMinute = windowEndMinute(draft.window);
  const maxDuration = MINUTES_PER_DAY - draft.window.startMinute;

  const handleSave = () => {
    if (!isRuleDraftSaveable(draft)) {
      setTargetsError(true);
      return;
    }
    setTargetsError(false);
    onSave();
  };

  return (
    <BottomSheet open={open} onClose={onClose} labelledBy="rule-editor-title">
      <div className="flex items-center justify-between px-4 pt-4 pb-1">
        <h2 id="rule-editor-title" className="text-lg font-semibold text-[oklch(92%_0.02_150)]">
          {draft._id ? t.rules.editRule : t.rules.newRuleEditor}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="icon-button"
          style={{ minWidth: 44, minHeight: 44, border: "none" }}
          aria-label={t.common.close}
        >
          <X size={18} />
        </button>
      </div>

      <div className="max-h-[64svh] overflow-y-auto px-4 pb-2">
        <section className="pb-4">
          <div className="flex items-baseline justify-between pb-1.5">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[oklch(52%_0.03_155)]">
              {t.rules.targetsSection}
            </h3>
            <span className="text-[11px] font-semibold text-[oklch(58%_0.03_155)]">
              {formatTemplate(t.rules.targetsLimitHint, { max: MAX_TARGETS_PER_RULE })}
              {" · "}
              {selected.size}/{MAX_TARGETS_PER_RULE}
            </span>
          </div>

          {targets.length === 0 ? (
            <button
              type="button"
              onClick={onCreateTarget}
              className="liquid-glass-dashed-cta flex w-full items-center gap-3 px-3 py-4 text-left"
            >
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[oklch(72%_0.09_155)] text-[oklch(12%_0.015_160)]">
                <Plus size={18} />
              </span>
              <span>
                <span className="block text-sm font-semibold text-[oklch(90%_0.02_150)]">{t.rules.createTargetCta}</span>
                <span className="block text-xs text-[oklch(64%_0.025_155)]">{t.rules.createTargetHint}</span>
              </span>
            </button>
          ) : (
            <div className="liquid-glass-inset grid gap-1 p-1">
              {targets.map((target) => (
                <TargetSelectRow
                  key={target._id}
                  target={target}
                  rules={rules}
                  targetsById={targetsById}
                  checked={selected.has(target._id)}
                  disabled={!selected.has(target._id) && atLimit}
                  onToggle={() => toggleTarget(target._id)}
                />
              ))}
              <button
                type="button"
                onClick={onCreateTarget}
                className="flex min-h-11 w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-sm font-semibold text-[oklch(72%_0.09_155)] transition-colors active:bg-[oklch(20%_0.02_165)]"
              >
                <span className="liquid-glass-icon-ring grid h-7 w-7 shrink-0 place-items-center">
                  <Plus size={14} />
                </span>
                {t.rules.newTarget}
              </button>
            </div>
          )}
          {limitNotice ? (
            <p className="pt-2 text-xs text-[oklch(72%_0.10_75)]" role="status">
              {formatTemplate(t.rules.targetsLimitReached, { max: MAX_TARGETS_PER_RULE })}
            </p>
          ) : null}
          {targetsError ? (
            <p className="pt-2 text-xs text-[oklch(72%_0.12_25)]" role="alert">
              {t.rules.targetsRequired}
            </p>
          ) : null}
        </section>

        <section className="pb-4">
          <h3 className="pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[oklch(52%_0.03_155)]">
            {t.rules.daysSection}
          </h3>
          <div className="flex justify-between gap-1.5">
            {WEEKDAYS_DISPLAY.map((day) => {
              const active = draft.days.includes(day.value);
              return (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => toggleDay(day.value)}
                  aria-pressed={active}
                  aria-label={day.long}
                  className={`grid h-10 min-w-[2.5rem] flex-1 place-items-center rounded-lg text-sm font-bold transition-colors ${
                    active
                      ? "bg-[oklch(72%_0.09_155)] text-[oklch(12%_0.015_160)]"
                      : "liquid-glass-day-inactive text-[oklch(70%_0.025_155)]"
                  }`}
                >
                  {day.short}
                </button>
              );
            })}
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between pb-2">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[oklch(52%_0.03_155)]">
              {t.rules.timeWindowSection}
            </h3>
            <ModeToggle mode={mode} onChange={setMode} t={t} />
          </div>

          <div className="liquid-glass-inset grid gap-3 p-3">
            <div className="grid grid-cols-2 gap-3">
              <TimeField label={t.rules.startLabel} value={formatDisplayMinuteOfDay(draft.window.startMinute)} onChange={setStart} />
              {mode === "endtime" ? (
                <TimeField
                  label={t.rules.endLabel}
                  value={formatDisplayMinuteOfDay(Math.min(endMinute, MINUTES_PER_DAY - 1))}
                  onChange={setEnd}
                />
              ) : (
                <div className="grid gap-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-[oklch(52%_0.03_155)]">
                    {t.rules.endsLabel}
                  </span>
                  <div className="liquid-glass-input flex min-h-11 items-center gap-2 rounded-lg px-3 text-base font-semibold text-[oklch(86%_0.03_155)]">
                    <Clock size={14} className="text-[oklch(58%_0.025_155)]" aria-hidden />
                    {formatDisplayMinuteOfDay(endMinute)}
                  </div>
                </div>
              )}
            </div>

            {mode === "duration" ? (
              <div className="grid gap-1.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[oklch(64%_0.025_155)]">{t.rules.durationMode}</span>
                  <span className="mono text-base font-semibold text-[oklch(86%_0.03_155)]">
                    {formatDurationMinutes(draft.window.durationMinutes, languageCode)}
                  </span>
                </div>
                <input
                  type="range"
                  min={Math.min(5, maxDuration)}
                  max={maxDuration}
                  step={5}
                  value={draft.window.durationMinutes}
                  onChange={(event) => setDuration(Number(event.target.value))}
                  className="w-full accent-[oklch(72%_0.09_155)]"
                  aria-label={t.rules.blockDurationAria}
                />
              </div>
            ) : null}
          </div>
          <p className="pt-2 text-xs text-[oklch(58%_0.025_155)]">{t.rules.windowNoMidnightNote}</p>
        </section>
      </div>

      <div className="liquid-glass-sheet-footer flex gap-2 px-4 pt-3">
        <button type="button" onClick={onClose} className="secondary-action flex-1">
          {t.common.cancel}
        </button>
        <button type="button" onClick={handleSave} className="primary-action flex-1" disabled={pending}>
          {draft._id ? t.rules.saveRule : t.rules.createRule}
        </button>
      </div>
    </BottomSheet>
  );
}

function ModeToggle({ mode, onChange, t }: { mode: WindowMode; onChange: (mode: WindowMode) => void; t: ReturnType<typeof getTranslations> }) {
  const prefersReducedMotion = useReducedMotion();
  const options: ReadonlyArray<{ value: WindowMode; label: string }> = [
    { value: "duration", label: t.rules.durationMode },
    { value: "endtime", label: t.rules.endTimeMode },
  ];
  return (
    <div className="liquid-glass-segmented">
      {options.map((option) => {
        const active = mode === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={active}
            className={`relative rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${active ? "text-[oklch(12%_0.015_160)]" : "text-[oklch(70%_0.025_155)]"}`}
          >
            {active ? (
              <motion.span
                layoutId="rule-window-mode"
                className="absolute inset-0 rounded-md bg-[oklch(72%_0.09_155)]"
                transition={prefersReducedMotion ? { duration: 0 } : { type: "spring", stiffness: 480, damping: 38 }}
              />
            ) : null}
            <span className="relative">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function TimeField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="grid gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-[oklch(52%_0.03_155)]">{label}</span>
      <input
        type="time"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="liquid-glass-input min-h-11 rounded-lg px-3 text-base text-[oklch(90%_0.02_150)]"
      />
    </label>
  );
}

export function TargetSelectRow({
  target,
  rules,
  targetsById,
  checked,
  disabled,
  onToggle,
}: {
  target: Target;
  rules?: Rule[];
  targetsById: Map<string, Target>;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const showSmartBlockBadge =
    rules !== undefined && targetDisplaysSmartBlockBadge(target._id, rules, targetsById);
  const inputId = `target-select-${target._id}`;

  return (
    <label
      htmlFor={inputId}
      className={`flex w-full min-h-11 cursor-pointer items-start gap-2 rounded-lg px-2 py-2 text-left transition-colors active:bg-[oklch(20%_0.02_165)] ${
        disabled ? "cursor-not-allowed opacity-50" : ""
      }`}
    >
      <input
        id={inputId}
        type="checkbox"
        className="sr-only"
        checked={checked}
        disabled={disabled}
        onChange={() => {
          if (!disabled) onToggle();
        }}
        aria-label={formatTemplate(t.rules.editTargetAria, { targetName: target.name })}
      />
      <TargetPreviewMeta target={target} dedupeTypedAndPickerApps={showSmartBlockBadge} />
      <TargetPreviewAssets target={target} />
      <span
        className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-md ${
          checked ? "bg-[oklch(72%_0.09_155)] text-[oklch(12%_0.015_160)]" : "liquid-glass-day-inactive text-transparent"
        }`}
        aria-hidden
      >
        <Check size={13} />
      </span>
    </label>
  );
}

export { targetContents } from "./TargetPreview";
